import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Select from "react-select";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import {
  MagnifyingGlassIcon,
  UserPlusIcon,
  UserIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  MapPinIcon,
} from "@heroicons/react/20/solid";

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
    "&:focus": {
      outline: "none",
      boxShadow: "none",
      border: "none",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
};

const SOURCE_OPTIONS = [
  { value: "Rocketreach", label: "Rocketreach" },
  { value: "Urban Toronto", label: "Urban Toronto" },
  { value: "Referral", label: "Referral" },
  { value: "Other", label: "Other" },
];

export default function ConvertToLeadModal({
  isOpen,
  onClose,
  prospect,
  onSuccess,
  onError,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [contactTab, setContactTab] = useState("search");

  // Contact search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // New contact form state
  const [newContact, setNewContact] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    companyAddress: {
      formattedAddress: "",
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
  });

  // Lead details state
  const [leadDetails, setLeadDetails] = useState({
    jobTitle: "",
    description: "",
    source: "Other",
    jobAddress: {
      formattedAddress: "",
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      latitude: null,
      longitude: null,
      placeId: "",
    },
  });

  const [markAsWon, setMarkAsWon] = useState(true);
  const [useCompanyAddressAsJobAddress, setUseCompanyAddressAsJobAddress] =
    useState(true);

  // Pre-fill from prospect data
  useEffect(() => {
    if (prospect && isOpen) {
      const nameParts = (prospect.contactName || "").split(" ");
      setNewContact({
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        email: prospect.email || "",
        phoneNumber: prospect.phoneNumber || "",
        companyAddress: {
          formattedAddress: prospect.address?.formattedAddress || "",
          street: prospect.address?.street || "",
          city: prospect.address?.city || "",
          state: prospect.address?.state || "",
          postalCode: prospect.address?.postalCode || "",
          country: prospect.address?.country || "",
        },
      });

      setLeadDetails({
        jobTitle: prospect.projectName || "",
        description: "",
        source: prospect.source || "Other",
        jobAddress: {
          formattedAddress: prospect.address?.formattedAddress || "",
          street: prospect.address?.street || "",
          city: prospect.address?.city || "",
          state: prospect.address?.state || "",
          postalCode: prospect.address?.postalCode || "",
          country: prospect.address?.country || "",
          latitude: prospect.address?.latitude || null,
          longitude: prospect.address?.longitude || null,
          placeId: prospect.address?.placeId || "",
        },
      });

      setSelectedContact(null);
      setSearchQuery("");
      setSearchResults([]);
      setContactTab("search");
      setUseCompanyAddressAsJobAddress(true);
    }
  }, [prospect, isOpen]);

  // Debounced contact search
  const searchContacts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/prospects/contacts/search?q=${encodeURIComponent(
          query
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setSearchResults(response.data.contacts);
      }
    } catch (error) {
      console.error("Error searching contacts:", error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchContacts(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchContacts]);

  // Handle company address change via Google Places
  const handleCompanyAddressChange = async (place) => {
    if (!place || !place.value || !place.value.place_id) {
      onError?.("Please select a valid address from the dropdown.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/opportunities/address/${place.value.place_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { address } = response.data;
        const newAddress = {
          formattedAddress: address.formattedAddress,
          street: address.street,
          city: address.city,
          state: address.state,
          postalCode: address.postalCode,
          country: address.country,
        };

        setNewContact((prev) => ({
          ...prev,
          companyAddress: newAddress,
        }));

        // If using company address as job address, update that too
        if (useCompanyAddressAsJobAddress) {
          setLeadDetails((prev) => ({
            ...prev,
            jobAddress: {
              ...newAddress,
              latitude: address.latitude,
              longitude: address.longitude,
              placeId: place.value.place_id,
            },
          }));
        }
      } else {
        throw new Error(
          response.data.message || "Failed to fetch address details"
        );
      }
    } catch (err) {
      onError?.(`Error fetching address details: ${err.message}`);
      setNewContact((prev) => ({
        ...prev,
        companyAddress: {
          ...prev.companyAddress,
          formattedAddress: place.label,
        },
      }));
    }
  };

  // Handle job address change via Google Places
  const handleJobAddressChange = async (place) => {
    if (!place || !place.value || !place.value.place_id) {
      onError?.("Please select a valid address from the dropdown.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/opportunities/address/${place.value.place_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { address } = response.data;
        setLeadDetails((prev) => ({
          ...prev,
          jobAddress: {
            formattedAddress: address.formattedAddress,
            street: address.street,
            city: address.city,
            state: address.state,
            postalCode: address.postalCode,
            country: address.country,
            latitude: address.latitude,
            longitude: address.longitude,
            placeId: place.value.place_id,
          },
        }));
      } else {
        throw new Error(
          response.data.message || "Failed to fetch address details"
        );
      }
    } catch (err) {
      onError?.(`Error fetching address details: ${err.message}`);
      setLeadDetails((prev) => ({
        ...prev,
        jobAddress: {
          ...prev.jobAddress,
          formattedAddress: place.label,
          placeId: place.value.place_id,
        },
      }));
    }
  };

  const handleNewContactChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setNewContact((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setNewContact((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleLeadDetailsChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setLeadDetails((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setLeadDetails((prev) => ({ ...prev, [field]: value }));
    }
  };

  // Handle toggling the "use company address as job address" checkbox
  const handleUseCompanyAddressToggle = (checked) => {
    setUseCompanyAddressAsJobAddress(checked);
    if (checked && newContact.companyAddress.formattedAddress) {
      setLeadDetails((prev) => ({
        ...prev,
        jobAddress: {
          ...newContact.companyAddress,
          latitude: null,
          longitude: null,
          placeId: "",
        },
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (contactTab === "search" && !selectedContact) {
      onError?.("Please select a contact or create a new one");
      return;
    }

    if (contactTab === "create") {
      if (!newContact.firstName || !newContact.lastName || !newContact.email) {
        onError?.("First name, last name, and email are required");
        return;
      }
    }

    if (!leadDetails.jobTitle) {
      onError?.("Job title is required");
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem("token");
      const payload = {
        jobTitle: leadDetails.jobTitle,
        jobAddress: leadDetails.jobAddress,
        description: leadDetails.description,
        source: leadDetails.source,
        markProspectAsWon: markAsWon,
      };

      if (contactTab === "search" && selectedContact) {
        payload.contactId = selectedContact._id;
      } else {
        payload.newContact = newContact;
      }

      const response = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/prospects/${prospect._id}/convert-to-lead`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        onSuccess?.(response.data);
        onClose();
      } else {
        if (response.data.existingContact) {
          onError?.(
            `A contact with this email already exists: ${response.data.existingContact.firstName} ${response.data.existingContact.lastName}`
          );
          setContactTab("search");
          setSelectedContact(response.data.existingContact);
        } else {
          onError?.(response.data.message || "Failed to convert prospect");
        }
      }
    } catch (error) {
      console.error("Error converting prospect:", error);
      onError?.(
        error.response?.data?.message ||
          "An error occurred while converting the prospect"
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-gray-800 rounded-xl shadow-xl">
          {/* Header */}
          <div className="border-b border-gray-600 px-6 py-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-200">
              Convert to Lead
              {prospect && (
                <span className="text-sm font-normal text-gray-400 ml-2">
                  — {prospect.projectName}
                </span>
              )}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-200"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <form
            onSubmit={handleSubmit}
            className="px-6 py-4 max-h-[70vh] overflow-y-auto"
          >
            {/* Contact Selection */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-orange-400 mb-3 flex items-center gap-2">
                <UserIcon className="h-4 w-4" />
                Contact Information
              </h3>

              {/* Tab buttons */}
              <div className="flex mb-4 bg-gray-700 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setContactTab("search")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    contactTab === "search"
                      ? "bg-orange-500 text-white"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  Find Existing
                </button>
                <button
                  type="button"
                  onClick={() => setContactTab("create")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    contactTab === "create"
                      ? "bg-orange-500 text-white"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  <UserPlusIcon className="h-4 w-4" />
                  Create New
                </button>
              </div>

              {/* Search Tab */}
              {contactTab === "search" && (
                <div className="space-y-3">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search contacts by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-md bg-gray-700 border border-gray-600 text-gray-200 placeholder-gray-400 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    />
                  </div>

                  {isSearching && (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-400"></div>
                    </div>
                  )}

                  {!isSearching && searchResults.length > 0 && (
                    <div className="border border-gray-600 rounded-lg divide-y divide-gray-600 max-h-48 overflow-y-auto">
                      {searchResults.map((contact) => (
                        <div
                          key={contact._id}
                          className={`p-3 cursor-pointer hover:bg-gray-700 transition-colors ${
                            selectedContact?._id === contact._id
                              ? "bg-orange-500/20 border-l-2 border-l-orange-500"
                              : ""
                          }`}
                          onClick={() => setSelectedContact(contact)}
                        >
                          <div className="font-medium text-gray-200">
                            {contact.firstName} {contact.lastName}
                          </div>
                          <div className="text-sm text-gray-400">
                            {contact.email}
                          </div>
                          {contact.relatedAccount && (
                            <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <BuildingOfficeIcon className="h-3 w-3" />
                              {contact.relatedAccount.companyName}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {!isSearching &&
                    searchQuery.length >= 2 &&
                    searchResults.length === 0 && (
                      <p className="text-sm text-gray-400 text-center py-4">
                        No contacts found. Try creating a new one.
                      </p>
                    )}

                  {selectedContact && (
                    <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                      <div className="text-sm font-medium text-orange-400">
                        Selected Contact
                      </div>
                      <div className="mt-1 text-gray-200">
                        {selectedContact.firstName} {selectedContact.lastName} —{" "}
                        {selectedContact.email}
                      </div>
                      {selectedContact.relatedAccount && (
                        <div className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                          <BuildingOfficeIcon className="h-3 w-3" />
                          {selectedContact.relatedAccount.companyName}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Create Tab */}
              {contactTab === "create" && (
                <div className="space-y-4">
                  {/* Company Name Display */}
                  {prospect?.companyName && (
                    <div className="p-3 bg-gray-700/50 rounded-lg border border-gray-600">
                      <div className="flex items-center gap-2">
                        <BuildingOfficeIcon className="h-4 w-4 text-orange-400" />
                        <span className="text-sm font-medium text-gray-400">
                          Account to be created:
                        </span>
                      </div>
                      <p className="mt-1 text-gray-200 font-medium">
                        {prospect.companyName}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={newContact.firstName}
                        onChange={(e) =>
                          handleNewContactChange("firstName", e.target.value)
                        }
                        placeholder="John"
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={newContact.lastName}
                        onChange={(e) =>
                          handleNewContactChange("lastName", e.target.value)
                        }
                        placeholder="Doe"
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={newContact.email}
                        onChange={(e) =>
                          handleNewContactChange("email", e.target.value)
                        }
                        placeholder="john@example.com"
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={newContact.phoneNumber}
                        onChange={(e) =>
                          handleNewContactChange("phoneNumber", e.target.value)
                        }
                        placeholder="+1 (555) 123-4567"
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      <BuildingOfficeIcon className="h-4 w-4 inline mr-1" />
                      Company Address
                    </label>
                    <p className="text-xs text-gray-500 mb-1">
                      This address will be used for both the Contact and Account
                    </p>
                    <GooglePlacesAutocomplete
                      apiKey="AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg"
                      autocompletionRequest={{
                        componentRestrictions: { country: ["ca", "us"] },
                      }}
                      selectProps={{
                        onChange: handleCompanyAddressChange,
                        placeholder: "Search for company address...",
                        value: newContact.companyAddress.formattedAddress
                          ? {
                              label: newContact.companyAddress.formattedAddress,
                              value: {
                                description:
                                  newContact.companyAddress.formattedAddress,
                              },
                            }
                          : null,
                        className: "mt-1",
                        styles: customSelectStyles,
                        isClearable: true,
                      }}
                    />
                  </div>

                  {/* Show parsed company address details */}
                  {newContact.companyAddress.formattedAddress && (
                    <div className="grid grid-cols-2 gap-4 p-3 bg-gray-700/50 rounded-lg">
                      <div>
                        <label className="block text-xs font-medium text-gray-500">
                          Street
                        </label>
                        <p className="text-sm text-gray-300">
                          {newContact.companyAddress.street || "—"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">
                          City
                        </label>
                        <p className="text-sm text-gray-300">
                          {newContact.companyAddress.city || "—"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">
                          State/Province
                        </label>
                        <p className="text-sm text-gray-300">
                          {newContact.companyAddress.state || "—"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">
                          Postal Code
                        </label>
                        <p className="text-sm text-gray-300">
                          {newContact.companyAddress.postalCode || "—"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Lead Details */}
            <div className="border-t border-gray-600 pt-6 mb-6">
              <h3 className="text-sm font-medium text-orange-400 mb-3">
                Lead Details
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    value={leadDetails.jobTitle}
                    onChange={(e) =>
                      handleLeadDetailsChange("jobTitle", e.target.value)
                    }
                    placeholder="Project name or job title"
                    className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Description
                  </label>
                  <textarea
                    value={leadDetails.description}
                    onChange={(e) =>
                      handleLeadDetailsChange("description", e.target.value)
                    }
                    placeholder="Brief description of the lead..."
                    rows={3}
                    className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Source
                  </label>
                  <Select
                    options={SOURCE_OPTIONS}
                    value={SOURCE_OPTIONS.find(
                      (option) => option.value === leadDetails.source
                    )}
                    onChange={(selected) =>
                      handleLeadDetailsChange(
                        "source",
                        selected ? selected.value : "Other"
                      )
                    }
                    className="mt-1"
                    styles={customSelectStyles}
                  />
                </div>

                {/* Job Address Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    <MapPinIcon className="h-4 w-4 inline mr-1" />
                    Job Address
                  </label>

                  {/* Checkbox to use company address */}
                  {contactTab === "create" &&
                    newContact.companyAddress.formattedAddress && (
                      <label className="flex items-center gap-2 mt-2 mb-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={useCompanyAddressAsJobAddress}
                          onChange={(e) =>
                            handleUseCompanyAddressToggle(e.target.checked)
                          }
                          className="rounded bg-gray-700 border-gray-600 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-300">
                          Use company address as job address
                        </span>
                      </label>
                    )}

                  {/* Show Google Places if not using company address or if on search tab */}
                  {(contactTab === "search" ||
                    !useCompanyAddressAsJobAddress) && (
                    <GooglePlacesAutocomplete
                      apiKey="AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg"
                      autocompletionRequest={{
                        componentRestrictions: { country: ["ca", "us"] },
                      }}
                      selectProps={{
                        onChange: handleJobAddressChange,
                        placeholder: "Search for job address...",
                        value: leadDetails.jobAddress.formattedAddress
                          ? {
                              label: leadDetails.jobAddress.formattedAddress,
                              value: {
                                description:
                                  leadDetails.jobAddress.formattedAddress,
                              },
                            }
                          : null,
                        className: "mt-1",
                        styles: customSelectStyles,
                        isClearable: true,
                      }}
                    />
                  )}
                </div>

                {/* Show parsed job address details */}
                {leadDetails.jobAddress.formattedAddress && (
                  <div className="grid grid-cols-2 gap-4 p-3 bg-gray-700/50 rounded-lg">
                    <div>
                      <label className="block text-xs font-medium text-gray-500">
                        Street
                      </label>
                      <p className="text-sm text-gray-300">
                        {leadDetails.jobAddress.street || "—"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500">
                        City
                      </label>
                      <p className="text-sm text-gray-300">
                        {leadDetails.jobAddress.city || "—"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500">
                        State/Province
                      </label>
                      <p className="text-sm text-gray-300">
                        {leadDetails.jobAddress.state || "—"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500">
                        Postal Code
                      </label>
                      <p className="text-sm text-gray-300">
                        {leadDetails.jobAddress.postalCode || "—"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mark as Won checkbox */}
            <div className="border-t border-gray-600 pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={markAsWon}
                  onChange={(e) => setMarkAsWon(e.target.checked)}
                  className="rounded bg-gray-700 border-gray-600 text-orange-500 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-300">
                  Mark prospect as "Won" after conversion
                </span>
              </label>
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-600 px-6 py-4 flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-500 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Converting...
                </>
              ) : (
                "Convert to Lead"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
